import { documentToHtmlString } from "@contentful/rich-text-html-renderer";
import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import {
  EQUIPMENT_TERMS,
  FINANCIAL_LINE_ITEMS,
  ORDER_SUMMARY_PAGE,
  PRIVACY_POLICY_PAGE,
  TERMS_OF_USE_PAGE,
} from "../../../constants/staticContent.js";

const MEDIA_TYPES_BY_EXTENSION = {
  jpeg: "image/jpeg",
  jpg: "image/jpeg",
  mp4: "video/mp4",
  png: "image/png",
  webm: "video/webm",
  webp: "image/webp",
};

// The hero media is served from S3 rather than Contentful, so it arrives as a
// bare URL with no contentType — the type comes from the file extension.
const getMediaTypeFromUrl = (url) => {
  const extension = url.split(/[?#]/)[0].split(".").pop().toLowerCase();

  return MEDIA_TYPES_BY_EXTENSION[extension] || null;
};

const HERO_MEDIA_URL = process.env.REACT_APP_HERO_MEDIA_URL || null;
const HERO_MEDIA_TYPE = HERO_MEDIA_URL
  ? getMediaTypeFromUrl(HERO_MEDIA_URL)
  : null;

export const contentApi = createApi({
  reducerPath: "contentApi",
  baseQuery: fetchBaseQuery({
    baseUrl: `${process.env.REACT_APP_CONTENTFUL_BASE_URL}${process.env.REACT_APP_CONTENTFUL_SPACE_ID}/environments/${process.env.REACT_APP_CONTENTFUL_ENVIRONMENT}/entries`,
    prepareHeaders: (headers) => {
      headers.set(
        "Authorization",
        `Bearer ${process.env.REACT_APP_CONTENTFUL_ACCESS_TOKEN}`,
      );

      return headers;
    },
  }),
  tagTypes: [
    "FAQs",
    "FeaturedFAQs",
    "FinancialLineItems",
    "StaticContent",
    "ProductReviews",
    "ProductCategories",
    "VrmOrderFunnel",
    "CustomerContent",
  ],
  endpoints: (build) => ({
    getCustomerContent: build.query({
      query: (coId) => ({
        url: "",
        params: {
          content_type: "customer",
          "fields.customerId": coId,
          // Activities are depth 1 and their images are depth 2.
          include: 2,
        },
      }),
      transformResponse: (response) => {
        const { items, includes } = response || {};
        const customer = items?.[0]?.fields;
        if (!customer) return null;

        const assetMap = Object.fromEntries(
          (includes?.Asset || []).map((asset) => [asset.sys.id, asset.fields]),
        );
        const entryMap = Object.fromEntries(
          (includes?.Entry || []).map((entry) => [entry.sys.id, entry]),
        );

        const activities = (customer.activities || [])
          .map(({ sys: { id } }) => entryMap[id])
          .filter(Boolean)
          .map(({ sys, fields }) => {
            const imageFile = fields.images && assetMap[fields.images.sys.id]?.file;
            const imageUrl = imageFile?.url ? `https:${imageFile.url}` : null;

            return {
              id: sys.id,
              name: fields.name,
              promoLine: fields.promoLine,
              description: fields.description
                ? documentToHtmlString(fields.description)
                : "",
              image: imageUrl,
            };
          });

        return {
          customerId: customer.customerId,
          activities,
        };
      },
      providesTags: (_result, _error, arg) => [
        { type: "CustomerContent", id: arg },
      ],
    }),
    getProductCategories: build.query({
      query: () => ({
        url: "",
        params: {
          content_type: "productCategory"
        },
      }),
      transformResponse: (response) => {
        const { items, includes } = response || {};
        const assetMap = Object.fromEntries(
          (includes?.Asset || []).map((asset) => [asset.sys.id, asset.fields]),
        );
        const entryMap = Object.fromEntries(
          (includes?.Entry || []).map((entry) => [entry.sys.id, entry.fields]),
        );

        return (items || [])
          .map(({ sys, fields }) => {
            const imageAsset = assetMap[fields.image.sys.id];
            const imageFile = imageAsset?.file;
            const imageUrl = imageFile?.url ? `https:${imageFile.url}` : null;

            const products = (fields.productList || []);

            return {
              id: sys.id,
              name: fields.name,
              headline: documentToHtmlString(fields.headline),
              slug: fields.slug,
              image: imageUrl,
              products: products,
              rank: fields.rank,
            };
          })
          .sort((a, b) => {
            return a.rank - b.rank;
          });
      },
      providesTags: ["ProductCategories"],
    }),
    getFinancialLineItems: build.query({
      query: () => ({
        url: "",
        params: { content_type: FINANCIAL_LINE_ITEMS },
      }),
      transformResponse: (response) => {
        return Object.fromEntries(
          (response?.items || []).map(({ fields }) => [
            fields.key,
            {
              label: fields.label,
              tooltipText: fields.tooltipText,
            },
          ]),
        );
      },
      providesTags: ["FinancialLineItems"],
    }),
    getAllProductReviews: build.query({
      query: () => ({
        url: "",
        params: {
          content_type: "productReviews",
          include: 2,
        },
      }),
      transformResponse: (response) => {
        const { items, includes } = response || {};
        const entryMap = Object.fromEntries(
          (includes?.Entry || []).map((entry) => [entry.sys.id, entry.fields]),
        );

        return (items || []).map(({ fields }) => ({
          productId: fields.productId,
          starRating: fields.starRating,
          reviewCount: fields.reviewCount,
          reviews: (fields.reviews || [])
            .map(({ sys: { id } }) => entryMap[id])
            .filter(Boolean),
        }));
      },
      providesTags: ["ProductReviews"],
    }),
    getFeaturedFAQs: build.query({
      query: () => {
        return {
          url: "",
          params: {
            content_type: "faq",
            "metadata.tags.sys.id[in]": "featured",
          },
        };
      },
      transformResponse: (entries) => {
        return entries?.items
          ?.map(({ fields }) => {
            const answer = documentToHtmlString(fields.answer);

            return {
              question: fields.question,
              answer,
            };
          })
          .reverse();
      },
      providesTags: ["FeaturedFAQs"],
    }),
    getAllFAQs: build.query({
      query: () => {
        return {
          url: "",
          params: {
            content_type: "faqs",
          },
        };
      },
      transformResponse: (response) => {
        const { includes, items } = response || {};
        const entries = includes?.Entry;
        const ids = items[0]?.fields.content.map(({ sys: { id } }) => id);

        entries.sort((a, b) => ids.indexOf(a.sys.id) - ids.indexOf(b.sys.id));

        return entries.map(({ fields, metadata }) => {
          const answer = documentToHtmlString(fields.answer);

          return {
            question: fields.question,
            answer,
            tags: metadata.tags?.map(({ sys }) => sys.id),
          };
        });
      },
      providesTags: ["FAQs"],
    }),
    getVrmOrderFunnel: build.query({
      query: () => ({
        url: "",
        params: {
          content_type: "vrmOrderFunnel",
          include: 1,
        },
      }),
      transformResponse: (response) => {
        const { items } = response || {};
        const entry = items?.[0]?.fields;
        if (!entry) return null;

        return {
          heroHeader: entry.heroHeader,
          heroSubheader: entry.heroSubheader,
          heroCta: entry.heroCta ? documentToHtmlString(entry.heroCta) : null,
          heroDisclaimer: entry.heroDisclaimer,
          heroMedia: HERO_MEDIA_URL,
          heroMediaType: HERO_MEDIA_TYPE,
          experiencesPhoneNumber: entry.experiencesPhoneNumber || null,
        };
      },
      providesTags: ["VrmOrderFunnel"],
    }),
    getStaticContent: build.query({
      query: (content_type) => {
        return {
          url: "",
          params: {
            content_type,
          },
        };
      },
      transformResponse: (entries, meta, arg) => {
        if (arg === TERMS_OF_USE_PAGE || arg === PRIVACY_POLICY_PAGE) {
          const entry = entries?.items[0]?.fields;

          return documentToHtmlString(entry?.content);
        } else if (arg === EQUIPMENT_TERMS) {
          const entry = entries?.includes?.Entry[0]?.fields;
          return documentToHtmlString(entry?.expanded);
        } else if (arg === ORDER_SUMMARY_PAGE) {
          const entry = entries?.items[0]?.fields;
          return {
            title: entry?.title,
            message: documentToHtmlString(entry?.message),
          };
        }
      },
      providesTags: (_result, _error, arg) => [
        { type: "StaticContent", id: arg },
      ],
    }),
  }),
});

export const {
  useGetFeaturedFAQsQuery,
  useGetAllFAQsQuery,
  useGetFinancialLineItemsQuery,
  useGetStaticContentQuery,
  useGetAllProductReviewsQuery,
  useGetProductCategoriesQuery,
  useGetVrmOrderFunnelQuery,
  useGetCustomerContentQuery,
} = contentApi;
